/**
 * Esri World Imagery Wayback: every archived release of the World Imagery basemap, each with its
 * own tiles and an imagery-metadata service that says, for any point, WHEN the picture there was
 * actually taken. Public, keyless, and served with CORS, so a release can be drawn onto a canvas
 * and saved as evidence.
 *
 * The release date is when Esri published the mosaic; the acquisition date (SRC_DATE2) is when the
 * satellite took the image at the plot - often months earlier. The acquisition date is the one
 * that proves "before" or "after" a harvest, so that is what is recorded.
 */
const CONFIG_URL = 'https://s3-us-west-2.amazonaws.com/config.maptiles.arcgis.com/waybackconfig.json';

export interface WaybackRelease {
  releaseNum: number;
  releaseDate: string;          // yyyy-mm-dd
  identifier: string;           // e.g. WB_2026_R02
  tileUrl: string;              // with {level}/{row}/{col}
  metadataUrl: string;
}

let cache: Promise<WaybackRelease[]> | null = null;

export function waybackReleases(): Promise<WaybackRelease[]> {
  cache ??= fetch(CONFIG_URL)
    .then((response) => {
      if (!response.ok) throw new Error('The imagery archive could not be reached.');
      return response.json() as Promise<Record<string, { itemTitle: string; itemURL: string; metadataLayerUrl: string; layerIdentifier: string }>>;
    })
    .then((config) => Object.entries(config)
      .map(([num, item]) => ({
        releaseNum: Number(num),
        releaseDate: /(\d{4}-\d{2}-\d{2})/.exec(item.itemTitle)?.[1] ?? '',
        identifier: item.layerIdentifier,
        tileUrl: item.itemURL,
        metadataUrl: item.metadataLayerUrl,
      }))
      .filter((release) => release.releaseDate)
      .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate)))
    .catch((error) => { cache = null; throw error; });
  return cache;
}

/** When the image at this point was taken, and by which sensor, for one release. */
export async function acquisitionAt(release: WaybackRelease, lon: number, lat: number): Promise<{ date: string; source: string | null; resolutionM: number | null } | null> {
  // The metadata layers go from 1.9 cm to 150 m resolution; field-scale imagery sits in 30 cm - 2.4 m.
  for (const layer of [4, 5, 6, 7, 3, 8]) {
    const url = `${release.metadataUrl}/${layer}/query?geometry=${lon},${lat}&geometryType=esriGeometryPoint&inSR=4326`
      + '&spatialRel=esriSpatialRelIntersects&outFields=SRC_DATE2,NICE_DESC,SRC_RES&returnGeometry=false&f=json';
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const body = await response.json() as { features?: Array<{ attributes: { SRC_DATE2?: number; NICE_DESC?: string; SRC_RES?: number } }> };
      const hit = body.features?.[0]?.attributes;
      if (hit?.SRC_DATE2) {
        return { date: new Date(hit.SRC_DATE2).toISOString().slice(0, 10), source: hit.NICE_DESC ?? null, resolutionM: hit.SRC_RES ?? null };
      }
    } catch {
      // try the next resolution
    }
  }
  return null;
}

type Ring = Array<[number, number]>;

function lonToX(lon: number, zoom: number) { return ((lon + 180) / 360) * 256 * 2 ** zoom; }
function latToY(lat: number, zoom: number) {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 256 * 2 ** zoom;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/** The outer rings of a Polygon or MultiPolygon, as [lon, lat] pairs. */
export function outerRings(geometry: { type: string; coordinates: unknown } | null | undefined): Ring[] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return [(geometry.coordinates as Ring[])[0] ?? []];
  if (geometry.type === 'MultiPolygon') return (geometry.coordinates as Ring[][]).map((polygon) => polygon[0] ?? []);
  return [];
}

export function centroidOf(rings: Ring[]): [number, number] | null {
  const points = rings.flat();
  if (points.length === 0) return null;
  return [points.reduce((sum, p) => sum + p[0], 0) / points.length, points.reduce((sum, p) => sum + p[1], 0) / points.length];
}

/**
 * Draw the plot boundary over one release of the imagery and return it as a PNG, with a caption
 * strip that says what it is: plot, release, acquisition date, and the stage (before / after).
 */
export async function renderSnapshot(release: WaybackRelease, rings: Ring[], caption: string[]): Promise<Blob> {
  const size = 768;
  const points = rings.flat();
  if (points.length === 0) throw new Error('The plot has no boundary to draw.');
  const lons = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  const [minLon, maxLon, minLat, maxLat] = [Math.min(...lons), Math.max(...lons), Math.min(...lats), Math.max(...lats)];

  // The largest zoom (up to 18) at which the boundary, with a margin, fits in the frame.
  let zoom = 18;
  while (zoom > 3) {
    const width = lonToX(maxLon, zoom) - lonToX(minLon, zoom);
    const height = latToY(minLat, zoom) - latToY(maxLat, zoom);
    if (width * 1.6 < size && height * 1.6 < size) break;
    zoom -= 1;
  }
  const centreX = (lonToX(minLon, zoom) + lonToX(maxLon, zoom)) / 2;
  const centreY = (latToY(maxLat, zoom) + latToY(minLat, zoom)) / 2;
  const left = centreX - size / 2;
  const top = centreY - size / 2;

  const canvas = document.createElement('canvas');
  const band = 22 * caption.length + 14;
  canvas.width = size;
  canvas.height = size + band;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot draw the image.');
  context.fillStyle = '#1f2937';
  context.fillRect(0, 0, size, size);

  const tiles: Array<Promise<void>> = [];
  for (let tx = Math.floor(left / 256); tx <= Math.floor((left + size) / 256); tx += 1) {
    for (let ty = Math.floor(top / 256); ty <= Math.floor((top + size) / 256); ty += 1) {
      const url = release.tileUrl.replace('{level}', String(zoom)).replace('{row}', String(ty)).replace('{col}', String(tx));
      tiles.push(loadImage(url).then((image) => { if (image) context.drawImage(image, tx * 256 - left, ty * 256 - top); }));
    }
  }
  await Promise.all(tiles);

  context.lineWidth = 3;
  context.strokeStyle = '#facc15';
  context.shadowColor = 'rgba(0,0,0,.7)';
  context.shadowBlur = 4;
  for (const ring of rings) {
    context.beginPath();
    ring.forEach(([lon, lat], index) => {
      const x = lonToX(lon, zoom) - left;
      const y = latToY(lat, zoom) - top;
      if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
    });
    context.closePath();
    context.stroke();
  }
  context.shadowBlur = 0;

  context.fillStyle = '#ffffff';
  context.fillRect(0, size, size, band);
  context.fillStyle = '#111827';
  context.font = '600 15px system-ui, sans-serif';
  caption.forEach((line, index) => {
    context.font = index === 0 ? '700 16px system-ui, sans-serif' : '14px system-ui, sans-serif';
    context.fillText(line, 12, size + 24 + index * 22);
  });

  try {
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('empty'))), 'image/png'));
  } catch {
    throw new Error('The imagery could not be captured (the tiles were refused). Try another release.');
  }
}
